import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, Filter, MoreVertical, Edit2, Trash2,
  RefreshCw, Mail, Printer, DollarSign, AlertCircle, Plus,
  FileText, ChevronDown, ChevronsUpDown, CheckCircle, XCircle,
  Clock, User, ChevronLeft, ChevronRight, Eye, Copy, Send, X, Calendar,
  Users
} from 'lucide-react';
import { apiFetch } from '../services/api';
import './Cobrancas.css';

// ⚠️ AJUSTE AQUI se o valor real do role "master" no seu banco for diferente
// (ex: 'admin', 'administrador', etc). Comparação é case-insensitive.
const MASTER_ROLES = ['master', 'admin'];

// ⚠️ Define qual campo identifica um cliente de forma única entre os pagamentos.
// Ordem de prioridade: customerId > customerCpf > customerEmail > customerName.
// Se o backend expõe um ID mais confiável (ex: p.customer?.id), ajuste aqui.
function getClientKey(p) {
  return (
    p.customerId ||
    p.customerCpf ||
    p.cpf ||
    p.customerEmail ||
    p.customerName ||
    'desconhecido'
  );
}

export default function CobrancasTodas() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ✅ Detecção de role baseada em user.role / user.roles (vindo de /auth/me)
  const userRole = (user?.role || user?.roles?.[0] || '').toLowerCase();
  const isMaster = MASTER_ROLES.includes(userRole);
  const isAtendente = !isMaster;

  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Estados específicos do fluxo de busca do Atendente
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [multiploClientes, setMultiploClientes] = useState(false);

  // Estados dos filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    vencimentoDe: '',
    vencimentoAte: '',
    recebimentoDe: '',
    recebimentoAte: '',
    tipos: [],
    situacoes: []
  });

  // Opções de filtros
  const tiposCobranca = [
    { id: 'SINGLE', label: 'Avulsas' },
    { id: 'SUBSCRIPTION', label: 'Assinaturas' },
    { id: 'INSTALLMENT', label: 'Parceladas' }
  ];

  const situacoesCobranca = [
    { id: 'PENDING', label: 'Aguardando pagamento' },
    { id: 'OVERDUE', label: 'Vencida' },
    { id: 'RECEIVED', label: 'Recebida' },
    { id: 'CONFIRMED', label: 'Confirmada' },
    { id: 'REFUNDED', label: 'Cobrança estornada' },
    { id: 'REFUND_REQUESTED', label: 'Estorno solicitado' },
    { id: 'PARTIALLY_REFUNDED', label: 'Cobrança estornada parcialmente' },
    { id: 'PAYMENT_IN_ANALYSIS', label: 'Pagamento em análise' },
    { id: 'CHARGEBACK_REQUESTED', label: 'Chargeback' }
  ];

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ✅ Carrega pagamentos automaticamente SOMENTE para Master.
  // Atendente não carrega nada até realizar uma busca.
  useEffect(() => {
    if (isAtendente) {
      setLoading(false);
      setPagamentos([]);
      return;
    }

    const carregarPagamentos = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch('/api/asaas/local-payments?limit=10000');
        const dados = response.data || [];
        console.log('📦 Total de cobranças carregadas:', dados.length);
        setPagamentos(dados);
      } catch (err) {
        console.error('Erro ao carregar:', err);
        setError('Não foi possível carregar as cobranças.');
      } finally {
        setLoading(false);
      }
    };
    carregarPagamentos();
  }, [isAtendente]);

  // ✅ Busca via API para Atendente — só exibe resultados se todos os
  // registros encontrados pertencerem ao MESMO cliente. Se a busca bater
  // em clientes diferentes, bloqueia a exibição e avisa o usuário.
  const realizarBusca = async () => {
    const term = searchTerm.trim();

    if (!term) {
      setPagamentos([]);
      setBuscaRealizada(true);
      setMultiploClientes(false);
      return;
    }

    setLoading(true);
    setError(null);
    setMultiploClientes(false);
    try {
      const response = await apiFetch(`/api/asaas/local-payments?limit=500&search=${encodeURIComponent(term)}`);
      let dados = response.data || [];

      const termLower = term.toLowerCase();
      const termDigits = term.replace(/\D/g, '');
      dados = dados.filter(p => {
        const name = (p.customerName || '').toLowerCase();
        const email = (p.customerEmail || '').toLowerCase();
        const cpf = (p.customerCpf || p.cpf || '').replace(/\D/g, '');
        const description = (p.description || '').toLowerCase();

        return name.includes(termLower) ||
               email.includes(termLower) ||
               description.includes(termLower) ||
               (termDigits && cpf.includes(termDigits));
      });

      // Verifica se os resultados pertencem todos ao mesmo cliente
      const chavesUnicas = new Set(dados.map(getClientKey));

      if (chavesUnicas.size > 1) {
        // Busca ambígua: encontrou mais de um cliente diferente
        setPagamentos([]);
        setMultiploClientes(true);
      } else {
        setPagamentos(dados);
        setMultiploClientes(false);
      }
      setBuscaRealizada(true);
    } catch (err) {
      console.error('Erro ao buscar:', err);
      setError('Não foi possível realizar a busca.');
      setPagamentos([]);
      setBuscaRealizada(true);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FUNÇÃO PARA EXCLUIR/CANCELAR COBRANÇA
  const handleExcluirCobranca = async (paymentId, customerName, status) => {
    if (status === 'RECEIVED' || status === 'CONFIRMED') {
      alert('Não é possível excluir uma cobrança que já foi recebida ou confirmada.');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir a cobrança de ${customerName}?\n\nEsta ação cancelará a cobrança no Asaas e a removerá do sistema.`)) {
      return;
    }

    setDeletingId(paymentId);
    try {
      const response = await apiFetch(`/api/asaas/payments/${paymentId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        alert('✅ Cobrança excluída com sucesso!');
        // Remove da lista localmente para atualização instantânea
        setPagamentos(prev => prev.filter(p => p.id !== paymentId));
      } else {
        alert('Erro: ' + (response.error || 'Não foi possível excluir a cobrança.'));
      }
    } catch (err) {
      console.error('Erro ao excluir:', err);
      alert('Erro ao excluir cobrança. Verifique o console.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '—';
    const d = new Date(dataStr + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
  };

  const formatarDataInput = (dataStr) => {
    if (!dataStr) return '';
    const d = new Date(dataStr + 'T00:00:00');
    return d.toISOString().split('T')[0];
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  const getStatusInfo = (status, dueDate) => {
    const hoje = new Date();
    const vencimento = dueDate ? new Date(dueDate + 'T00:00:00') : null;
    const vencido = vencimento && vencimento < hoje && status !== 'RECEIVED';

    if (status === 'RECEIVED') {
      return {
        icon: <CheckCircle size={14} className="status-icon received" />,
        text: 'Recebida',
        class: 'received'
      };
    } else if (status === 'CONFIRMED') {
      return {
        icon: <CheckCircle size={14} className="status-icon confirmed" />,
        text: 'Confirmada',
        class: 'confirmed'
      };
    } else if (vencido) {
      return {
        icon: <XCircle size={14} className="status-icon overdue" />,
        text: 'Vencida',
        class: 'overdue'
      };
    } else if (status === 'PENDING') {
      return {
        icon: <Clock size={14} className="status-icon pending" />,
        text: 'Aguardando pagamento',
        class: 'pending'
      };
    }
    return { icon: null, text: status || '—', class: '' };
  };

  const getBillingTypeText = (type) => {
    const types = {
      'SINGLE': 'Avulsa',
      'SUBSCRIPTION': 'Assinatura',
      'INSTALLMENT': 'Parcelada',
      'BOLETO': 'Boleto Bancário',
      'CREDIT_CARD': 'Cartão de Crédito',
      'PIX': 'PIX',
      'DEBIT_CARD': 'Cartão de Débito',
    };
    return types[type] || type || '—';
  };

  // Handler para toggle de checkbox
  const toggleFilter = (type, value) => {
    setFilters(prev => {
      const current = prev[type];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [type]: updated };
    });
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFilters({
      vencimentoDe: '',
      vencimentoAte: '',
      recebimentoDe: '',
      recebimentoAte: '',
      tipos: [],
      situacoes: []
    });
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    setShowFilters(false);
    setCurrentPage(1);
  };

  // Contar filtros ativos
  const filtrosAtivosCount = useMemo(() => {
    let count = 0;
    if (filters.vencimentoDe) count++;
    if (filters.vencimentoAte) count++;
    if (filters.recebimentoDe) count++;
    if (filters.recebimentoAte) count++;
    count += filters.tipos.length;
    count += filters.situacoes.length;
    return count;
  }, [filters]);

  // Filtrar pagamentos (aplica-se sobre o que já está em `pagamentos`,
  // que para Atendente já vem restrito a um único cliente)
  const filteredPayments = useMemo(() => {
    let result = [...pagamentos];

    // Filtro de busca (mantido para refinar dentro do próprio conjunto já carregado)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(p => {
        const name = (p.customerName || '').toLowerCase();
        const email = (p.customerEmail || '').toLowerCase();
        const description = (p.description || '').toLowerCase();
        const cpfDigitos = String(p.customerCpf || '').replace(/\D/g, '');
        const cpfFormatado = cpfDigitos.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
        const cpf = [cpfDigitos, cpfFormatado].filter(Boolean).join(' ').toLowerCase();

        return name.includes(search) ||
               email.includes(search) ||
               description.includes(search) ||
               cpf.includes(search);
      });
    }

    // Filtro por período de vencimento
    if (filters.vencimentoDe) {
      const de = new Date(filters.vencimentoDe + 'T00:00:00');
      result = result.filter(p => {
        if (!p.dueDate) return false;
        const dataVenc = new Date(p.dueDate + 'T00:00:00');
        return dataVenc >= de;
      });
    }

    if (filters.vencimentoAte) {
      const ate = new Date(filters.vencimentoAte + 'T23:59:59');
      result = result.filter(p => {
        if (!p.dueDate) return false;
        const dataVenc = new Date(p.dueDate + 'T00:00:00');
        return dataVenc <= ate;
      });
    }

    // Filtro por período de recebimento
    if (filters.recebimentoDe || filters.recebimentoAte) {
      result = result.filter(p => {
        if (!p.paymentDate && !p.receivedDate) return false;
        const dataPgto = new Date((p.paymentDate || p.receivedDate) + 'T00:00:00');

        if (filters.recebimentoDe) {
          const de = new Date(filters.recebimentoDe + 'T00:00:00');
          if (dataPgto < de) return false;
        }

        if (filters.recebimentoAte) {
          const ate = new Date(filters.recebimentoAte + 'T23:59:59');
          if (dataPgto > ate) return false;
        }

        return true;
      });
    }

    // Filtro por tipos de cobrança
    if (filters.tipos.length > 0) {
      result = result.filter(p => {
        if (!p.subscriptionType) return filters.tipos.includes('SINGLE');
        return filters.tipos.includes(p.subscriptionType);
      });
    }

    // Filtro por situações
    if (filters.situacoes.length > 0) {
      result = result.filter(p => filters.situacoes.includes(p.status));
    }

    return result;
  }, [pagamentos, searchTerm, filters]);

  // Paginação
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPayments = filteredPayments.slice(startIndex, endIndex);

  // Cálculos
  const totalValor = currentPayments.reduce((sum, p) => sum + (p.value || 0), 0);
  const totalGeral = filteredPayments.reduce((sum, p) => sum + (p.value || 0), 0);

  // Resetar página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // Estados de exibição para o fluxo do Atendente
  const mostrarPromptBuscaInicial = isAtendente && !buscaRealizada && !loading && !error;
  const mostrarAvisoMultiploClientes = isAtendente && buscaRealizada && multiploClientes && !loading;
  const mostrarConteudo = (!isAtendente || (buscaRealizada && !multiploClientes)) && !mostrarPromptBuscaInicial;

  return (
    <div className="cobrancas-page-container">
      <div className="cobrancas-page-header">
        <h1 className="cobrancas-page-title">Todas</h1>
      </div>

      <div className="cobrancas-card">
        {/* Toolbar */}
        <div className="cobrancas-toolbar">
          <div className="cobrancas-toolbar-left">
            <div className="cobrancas-search-box">
              <input
                type="text"
                placeholder={isAtendente ? "Digite o nome, email ou CPF do cliente para buscar" : "Procurar por nome ou email do cliente"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (isAtendente && e.key === 'Enter') {
                    realizarBusca();
                  }
                }}
                className="cobrancas-search-input"
              />
              <button
                className="cobrancas-search-btn"
                title="Buscar"
                onClick={() => { if (isAtendente) realizarBusca(); }}
              >
                <Search size={16} />
              </button>
            </div>
            {isMaster && (
              <button
                className={`cobrancas-btn-filter ${filtrosAtivosCount > 0 ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter size={16} />
                Filtros
                {filtrosAtivosCount > 0 && (
                  <span className="filter-badge">{filtrosAtivosCount}</span>
                )}
                <ChevronDown size={16} className={showFilters ? 'rotated' : ''} />
              </button>
            )}
          </div>
          <div className="cobrancas-toolbar-right">
            {isMaster && (
              <button className="cobrancas-btn-batch">
                <MoreVertical size={16} />
                Ações em lote
                <ChevronDown size={16} />
              </button>
            )}
            <button className="cobrancas-btn-add" onClick={() => navigate('/gerar-cobranca')}>
              <Plus size={18} />
              Adicionar cobrança
            </button>
          </div>
        </div>

        {/* Modal de Filtros (somente Master) */}
        {isMaster && showFilters && (
          <div className="cobrancas-filter-modal">
            <div className="cobrancas-filter-content">
              <div className="cobrancas-filter-header">
                <h3>Filtros</h3>
                <button className="cobrancas-filter-close" onClick={() => setShowFilters(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="cobrancas-filter-body">
                <div className="filter-periods-grid">
                  <div className="filter-section">
                    <h4 className="filter-section-title">Período de vencimento</h4>
                    <div className="filter-date-range">
                      <div className="filter-date-input">
                        <Calendar size={14} />
                        <input
                          type="date"
                          value={filters.vencimentoDe}
                          onChange={(e) => setFilters({...filters, vencimentoDe: e.target.value})}
                          placeholder="--/--/----"
                        />
                      </div>
                      <span className="filter-date-separator">até</span>
                      <div className="filter-date-input">
                        <Calendar size={14} />
                        <input
                          type="date"
                          value={filters.vencimentoAte}
                          onChange={(e) => setFilters({...filters, vencimentoAte: e.target.value})}
                          placeholder="--/--/----"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="filter-section">
                    <h4 className="filter-section-title">Período de recebimento</h4>
                    <div className="filter-date-range">
                      <div className="filter-date-input">
                        <Calendar size={14} />
                        <input
                          type="date"
                          value={filters.recebimentoDe}
                          onChange={(e) => setFilters({...filters, recebimentoDe: e.target.value})}
                          placeholder="--/--/----"
                        />
                      </div>
                      <span className="filter-date-separator">até</span>
                      <div className="filter-date-input">
                        <Calendar size={14} />
                        <input
                          type="date"
                          value={filters.recebimentoAte}
                          onChange={(e) => setFilters({...filters, recebimentoAte: e.target.value})}
                          placeholder="--/--/----"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="filter-columns">
                  <div className="filter-column">
                    <h4 className="filter-section-title">Tipos de cobrança</h4>
                    <div className="filter-checkboxes">
                      {tiposCobranca.map(tipo => (
                        <label key={tipo.id} className="filter-checkbox">
                          <input
                            type="checkbox"
                            checked={filters.tipos.includes(tipo.id)}
                            onChange={() => toggleFilter('tipos', tipo.id)}
                          />
                          <span>{tipo.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="filter-column">
                    <h4 className="filter-section-title">Situações das cobranças</h4>
                    <div className="filter-checkboxes filter-checkboxes-grid">
                      {situacoesCobranca.map(situacao => (
                        <label key={situacao.id} className="filter-checkbox">
                          <input
                            type="checkbox"
                            checked={filters.situacoes.includes(situacao.id)}
                            onChange={() => toggleFilter('situacoes', situacao.id)}
                          />
                          <span>{situacao.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="cobrancas-filter-footer">
                <div className="cobrancas-filter-footer-left">
                  <button className="cobrancas-filter-btn-secondary" onClick={limparFiltros}>
                    Limpar
                  </button>
                </div>
                <div className="cobrancas-filter-footer-right">
                  <button className="cobrancas-filter-btn-advanced">
                    Filtros avançados
                  </button>
                  <button className="cobrancas-filter-btn-primary" onClick={aplicarFiltros}>
                    Aplicar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="cobrancas-loading">
            <RefreshCw size={24} className="spinning" />
            <p>Carregando cobranças...</p>
          </div>
        )}

        {error && (
          <div className="cobrancas-error">
            <AlertCircle size={24} />
            <p>{error}</p>
          </div>
        )}

        {/* Prompt inicial de busca — somente Atendente, antes de buscar */}
        {mostrarPromptBuscaInicial && (
          <div className="cobrancas-empty">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              padding: '40px 20px',
              color: '#64748b'
            }}>
              <Search size={48} style={{ color: '#94a3b8' }} />
              <h3 style={{ margin: 0, color: '#334155', fontSize: '18px' }}>
                Realize uma busca para visualizar as cobranças
              </h3>
              <p style={{ margin: 0, textAlign: 'center', maxWidth: '400px', fontSize: '14px' }}>
                Digite o nome, email ou CPF do cliente no campo de busca acima e pressione Enter ou clique no ícone de lupa.
              </p>
            </div>
          </div>
        )}

        {/* Aviso de busca ambígua — encontrou mais de um cliente diferente */}
        {mostrarAvisoMultiploClientes && (
          <div className="cobrancas-empty">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              padding: '40px 20px',
              color: '#92400e'
            }}>
              <Users size={48} style={{ color: '#f59e0b' }} />
              <h3 style={{ margin: 0, color: '#78350f', fontSize: '18px' }}>
                Sua busca encontrou mais de um cliente
              </h3>
              <p style={{ margin: 0, textAlign: 'center', maxWidth: '440px', fontSize: '14px' }}>
                Para sua segurança, só é possível visualizar cobranças de um cliente por vez.
                Refine a busca com o CPF completo ou o email exato do cliente.
              </p>
            </div>
          </div>
        )}

        {!loading && !error && mostrarConteudo && currentPayments.length > 0 && (
          <>
            <div className="cobrancas-table-wrapper">
              <table className="cobrancas-table-modern">
                <thead>
                  <tr>
                    <th className="col-nome">Nome</th>
                    <th className="col-valor">Valor</th>
                    <th className="col-descricao">Descrição</th>
                    <th className="col-pagamento">Forma de pagamento</th>
                    <th className="col-vencimento">Data de vencimento</th>
                    <th className="col-acoes">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPayments.map((p) => {
                    const statusInfo = getStatusInfo(p.status, p.dueDate);
                    const isPaid = p.status === 'RECEIVED' || p.status === 'CONFIRMED';

                    return (
                      <tr key={p.id} className="cobrancas-table-row">
                        <td className="col-nome">
                          <div className="cliente-cell">
                            <User size={16} className={`cliente-avatar-icon ${statusInfo.class}`} />
                            <span className="cliente-nome">{p.customerName || '—'}</span>
                          </div>
                        </td>
                        <td className="col-valor">
                          <span className="valor-text">{formatarMoeda(p.value)}</span>
                        </td>
                        <td className="col-descricao">
                          <span className="descricao-text">
                            {p.description || 'Descrição não informada'}
                          </span>
                        </td>
                        <td className="col-pagamento">
                          <span className="pagamento-text">{getBillingTypeText(p.subscriptionType || p.billingType)}</span>
                        </td>
                        <td className="col-vencimento">
                          <div className="vencimento-cell">
                            <span className="vencimento-data">{formatarData(p.dueDate)}</span>
                            {statusInfo.icon}
                          </div>
                        </td>
                        <td className="col-acoes">
                          <div className="acoes-buttons">
                            <button className="acao-btn acao-btn-muted" title="Receber">
                              <DollarSign size={14} />
                            </button>
                            <button className="acao-btn acao-btn-muted" title="Segunda via">
                              <RefreshCw size={14} />
                            </button>
                            <button className="acao-btn acao-btn-info" title="Editar">
                              <Edit2 size={14} />
                            </button>
                            <button className="acao-btn acao-btn-info" title="Imprimir">
                              <Printer size={14} />
                            </button>
                            <button className="acao-btn acao-btn-info" title="Enviar email">
                              <Mail size={14} />
                            </button>

                            {/* ✅ BOTÃO DE EXCLUIR */}
                            <button
                              className="acao-btn delete"
                              title={isPaid ? 'Não é possível excluir cobranças recebidas' : 'Excluir'}
                              onClick={() => handleExcluirCobranca(p.id, p.customerName, p.status)}
                              disabled={deletingId === p.id || isPaid}
                              style={{
                                opacity: isPaid ? 0.4 : 1,
                                cursor: isPaid ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {deletingId === p.id ? (
                                <RefreshCw size={14} className="spinning" />
                              ) : (
                                <Trash2 size={14} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer com Paginação */}
            <div className="cobrancas-footer">
              <div className="footer-info">
                <span>
                  Mostrando {startIndex + 1}-{Math.min(endIndex, filteredPayments.length)} de {filteredPayments.length} cobranças
                  {searchTerm && ` (filtrado de ${pagamentos.length})`}
                  • Total: {formatarMoeda(totalGeral)}
                </span>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pag-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        className={`pag-btn ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    className="pag-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {!loading && !error && mostrarConteudo && currentPayments.length === 0 && (
          <div className="cobrancas-empty">
            <FileText size={48} />
            <p>
              {searchTerm || filtrosAtivosCount > 0
                ? 'Nenhuma cobrança encontrada para os filtros selecionados'
                : 'Nenhuma cobrança encontrada.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
